/**
 * Shell 2.0.0: sidebar, visor, admin, atrás del navegador → Inicio.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { useData } from '../DataContext';
import { useNavigation } from '../NavigationContext';

import AdminPanel from './AdminPanel';
import AccountPanel from './AccountPanel';
import DashboardGrid from './DashboardGrid';
import { PEOPLE_HUB_VIEWS } from './admin/PeopleHub';
import companyLogo from '../assets/logo.png';
import BellIcon from '../assets/icons/bell.svg';
import { FaUsers, FaFileAlt, FaChartLine, FaCogs, FaDatabase } from 'react-icons/fa';
import CustomReportRenderer from './CustomReportRenderer';
import ReportEmbedFrame from './ReportEmbedFrame';
import AssistantBuddy from './assistant/AssistantBuddy';
import AvatarWorld from '../CM_Avatar_World';
import { isCustomBuiltReport, isExternalEmbedUrl, shouldUseIframe, shouldUseTestigoPdf } from '../reportViewUtils';
import { ink } from './admin/studioUi';
import { useIsNarrow, isNarrowViewport, VH, NARROW_MAX } from '../useMediaQuery';

const APP_VERSION = "2.2.0";

function activateOnKey(onClick) {
    return (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick();
        }
    };
}

function NavBtn({ active, onClick, children, depth = 0 }) {
    return (
        <div
            role="button"
            tabIndex={0}
            className={`cm-side-btn${active ? ' is-on' : ''}`}
            onClick={onClick}
            onKeyDown={activateOnKey(onClick)}
            style={{
                ...styles.navBtn,
                paddingLeft: 12 + depth * 12,
                ...(active ? styles.navBtnOn : {}),
            }}
        >
            {children}
        </div>
    );
}

function AdminGroup({ icon, label, open, onToggle, children }) {
    return (
        <div>
            <div
                role="button"
                tabIndex={0}
                className="cm-side-fold"
                onClick={onToggle}
                onKeyDown={activateOnKey(onToggle)}
                style={styles.sectionBtn}
            >
                <span style={styles.sectionLabel}>
                    {icon}
                    <span>{label}</span>
                </span>
                <span style={styles.chevron}>{open ? '▾' : '▸'}</span>
            </div>
            {open && <div style={styles.groupBody}>{children}</div>}
        </div>
    );
}

function findReportPath(menu, reportId) {
    if (!reportId) return null;
    for (const client of Object.keys(menu)) {
        for (const project of Object.keys(menu[client])) {
            if (menu[client][project].some((report) => report.id === reportId)) {
                return { client, project };
            }
        }
    }
    return null;
}

function ReportsTree({ menu, selectedId, onOpen }) {
    const clients = useMemo(() => Object.keys(menu).sort((a, b) => a.localeCompare(b, 'es')), [menu]);
    const [openClient, setOpenClient] = useState(null);
    const [openProject, setOpenProject] = useState(null);

    useEffect(() => {
        const path = findReportPath(menu, selectedId);
        if (!path) return;
        setOpenClient(path.client);
        setOpenProject(path.project);
    }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps -- only follow the open report

    const total = useMemo(
        () => clients.reduce((sum, client) => (
            sum + Object.values(menu[client] || {}).reduce((inner, list) => inner + list.length, 0)
        ), 0),
        [clients, menu]
    );

    return (
        <div style={styles.treeWrap}>
            <div style={styles.treeHead}>
                <span>Reportes</span>
                <span style={styles.treeCount}>{total}</span>
            </div>
            <div style={styles.tree}>
                {clients.map((client) => {
                    const projects = Object.keys(menu[client]).sort((a, b) => a.localeCompare(b, 'es'));
                    const clientOpen = openClient === client;
                    return (
                        <div key={client}>
                            <div
                                role="button"
                                tabIndex={0}
                                className="cm-side-fold"
                                onClick={() => {
                                    setOpenClient(clientOpen ? null : client);
                                    if (clientOpen) setOpenProject(null);
                                }}
                                onKeyDown={activateOnKey(() => {
                                    setOpenClient(clientOpen ? null : client);
                                    if (clientOpen) setOpenProject(null);
                                })}
                                style={{ ...styles.treeClient, ...(clientOpen ? styles.treeClientOpen : {}) }}
                            >
                                <span style={styles.chevron}>{clientOpen ? '▾' : '▸'}</span>
                                <span style={styles.treeLabel}>{client}</span>
                            </div>
                            {clientOpen && (
                                <div style={styles.treeBranch}>
                                    {projects.map((project) => {
                                        const projectOpen = openProject === project;
                                        const reports = menu[client][project];
                                        return (
                                            <div key={`${client}:${project}`}>
                                                <div
                                                    role="button"
                                                    tabIndex={0}
                                                    className="cm-side-fold"
                                                    onClick={() => setOpenProject(projectOpen ? null : project)}
                                                    onKeyDown={activateOnKey(() => setOpenProject(projectOpen ? null : project))}
                                                    style={{ ...styles.treeProject, ...(projectOpen ? styles.treeProjectOpen : {}) }}
                                                >
                                                    <span style={styles.chevron}>{projectOpen ? '▾' : '▸'}</span>
                                                    <span style={styles.treeLabel}>{project}</span>
                                                </div>
                                                {projectOpen && (
                                                    <div style={styles.treeLeaves}>
                                                        {reports.map((report) => {
                                                            const active = selectedId === report.id;
                                                            return (
                                                                <div
                                                                    key={report.id}
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    className={`cm-side-leaf${active ? ' is-on' : ''}`}
                                                                    onClick={() => onOpen(report)}
                                                                    onKeyDown={activateOnKey(() => onOpen(report))}
                                                                    style={{ ...styles.treeLeaf, ...(active ? styles.treeLeafOn : {}) }}
                                                                    title={report.reporteNombre}
                                                                >
                                                                    {report.reporteNombre}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

const SIDEBAR_CSS = `
.cm-side-btn, .cm-side-plain, .cm-side-fold, .cm-side-leaf {
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}
.cm-side-btn:hover, .cm-side-fold:hover, .cm-side-leaf:hover {
  background-color: rgba(255,255,255,0.07);
  color: #fff;
}
.cm-side-btn:focus, .cm-side-fold:focus, .cm-side-leaf:focus,
.cm-side-btn:focus-visible, .cm-side-fold:focus-visible, .cm-side-leaf:focus-visible,
.cm-side-btn:active, .cm-side-fold:active, .cm-side-leaf:active {
  outline: none;
  color: #fff;
  background-color: transparent;
}
.cm-side-btn.is-on, .cm-side-leaf.is-on,
.cm-side-btn.is-on:hover, .cm-side-leaf.is-on:hover,
.cm-side-btn.is-on:focus, .cm-side-leaf.is-on:focus,
.cm-side-btn.is-on:active, .cm-side-leaf.is-on:active {
  color: #fff;
  background-color: rgba(94, 53, 177, 0.52);
}
.cm-side-plain:focus, .cm-side-plain:active {
  outline: none;
  color: inherit;
}
@media (max-width: ${NARROW_MAX}px) {
  .cm-side-btn, .cm-side-fold { min-height: 44px; box-sizing: border-box; }
  .cm-side-leaf { padding-top: 11px !important; padding-bottom: 11px !important; font-size: 0.88rem !important; }
}
`;

/* Títulos de la barra superior móvil por subvista del panel admin. */
const ADMIN_TITLES = {
    biblioteca: 'Biblioteca',
    'report-details': 'Ficha del reporte',
    clientes: 'Clientes y proyectos',
    'creador-reportes': 'Creador de reportes',
    'repos-social': 'Redes Sociales',
    'repos-perfiles': 'Perfiles de Redes Sociales',
    estadisticas: 'Estadísticas de reportes',
    'user-statistics': 'Estadísticas de usuarios',
    mantenimiento: 'Mantenimiento',
    auditoria: 'Auditoría',
    'inventario-reportes': 'Inventario de reportes',
    'ajustes-reportes': 'Ajustes de reportes',
    'email-templates': 'Plantillas de correo',
    notifications: 'Notificaciones',
    usuarios: 'Usuarios',
    grupos: 'Grupos',
    roles: 'Roles',
    permisos: 'Permisos',
};

const MainLayout = () => {
    const { user, userData, userPermissions, userRole, impersonation, stopImpersonation, handleSignOut, fetchWithAuth, API_URL, onUserDataUpdate } = useAuth();
    const { dashboards, unreadNotificationsCount } = useData();
    const { view, setView, adminSubView, setAdminSubView, selectedUrl, setSelectedUrl, selectedReportId, openReport, goHome, handleViewReportDetailsById } = useNavigation();
    const selectedReport = selectedReportId
        ? dashboards.find(item => item.id === selectedReportId)
        : dashboards.find(item => item.dashboardUrl === selectedUrl && !isCustomBuiltReport(item));

    const narrow = useIsNarrow();
    /* En móvil el menú arranca cerrado y se cierra solo al navegar. */
    const [isSidebarHidden, setIsSidebarHidden] = useState(() => isNarrowViewport());

    useEffect(() => {
        if (narrow) setIsSidebarHidden(true);
    }, [narrow, view, adminSubView, selectedUrl, selectedReportId]);

    /* El contenedor principal es el que hace scroll: al cambiar de vista vuelve arriba
       (antes la vista nueva heredaba la posición de la anterior). */
    const mainContentRef = useRef(null);
    useEffect(() => {
        if (mainContentRef.current) mainContentRef.current.scrollTop = 0;
    }, [view, adminSubView, selectedUrl, selectedReportId]);
    const [adminMenuOpen, setAdminMenuOpen] = useState({ reportes: true, estadisticas: false, administracion: false });

    useEffect(() => {
        if (view === 'admin' && (adminSubView === 'mantenimiento' || adminSubView === 'auditoria' || adminSubView === 'email-templates' || adminSubView === 'inventario-reportes' || adminSubView === 'ajustes-reportes')) {
            setAdminMenuOpen({ reportes: false, estadisticas: false, administracion: true });
        }
    }, [view, adminSubView]);

    const handleAdminMenuToggle = (menuKey) => {
        setAdminMenuOpen(prev => {
            const newState = { reportes: false, estadisticas: false, administracion: false, repositorios: false };
            if (!prev[menuKey]) {
                newState[menuKey] = true;
            }
            return newState;
        });
    };

    useEffect(() => {
        window.history.pushState({ appNav: 'guard' }, '');
        const onPopState = () => {
            goHome();
            window.history.pushState({ appNav: 'guard' }, '');
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [goHome]);

    useEffect(() => {
        if (user && dashboards.length > 0) {
            const reportId = sessionStorage.getItem('redirectReportId');
            if (reportId) {
                sessionStorage.removeItem('redirectReportId');
                handleViewReportDetailsById(reportId);
            }
        }
    }, [user, dashboards, handleViewReportDetailsById]);

    useEffect(() => {
        if (selectedReport?.id) {
            const viewingUserId = impersonation ? impersonation.uid : user.uid;
            if (viewingUserId) {
                fetchWithAuth(`${API_URL}/api/reports/${selectedReport.id}/log-view`, { method: 'POST' })
                    .catch(err => console.error("Failed to track view:", err));
            }
        }
    }, [selectedReport?.id, dashboards, user, impersonation, API_URL, fetchWithAuth]);

    const menuEstructurado = useMemo(() => dashboards.reduce((acc, item) => {
        if (item.isDraft) return acc;
        const cliente = item.cliente || 'Otros';
        const proyecto = item.proyecto || 'General';
        if (!acc[cliente]) acc[cliente] = {};
        if (!acc[cliente][proyecto]) acc[cliente][proyecto] = [];
        if (!acc[cliente][proyecto].some((report) => report.id === item.id)) acc[cliente][proyecto].push(item);
        return acc;
    }, {}), [dashboards]);

    const drawerOpen = narrow && !isSidebarHidden;
    const mobileTitle = view === 'account'
        ? 'Mi cuenta'
        : view === 'admin'
            ? (ADMIN_TITLES[adminSubView] || 'Panel admin')
            : (selectedReport?.reporteNombre || (selectedUrl || selectedReportId ? 'Reporte' : 'Inicio'));

    return (
        <div style={narrow ? { ...styles.appContainer, height: `100${VH}`, width: '100%' } : styles.appContainer}>
            <style>{SIDEBAR_CSS}</style>
            <AssistantBuddy />
            {impersonation && !isSidebarHidden && (
                <div style={styles.impersonationBanner}>
                    <p>👁️ Viendo como <strong>{impersonation.email}</strong></p>
                    <button onClick={() => {
                        stopImpersonation();
                        setView('admin');
                        setAdminSubView('usuarios');
                    }} style={styles.impersonationButton}>Volver a mi cuenta</button>
                </div>
            )}

            {narrow && (
                <header style={styles.topBar}>
                    <button
                        type="button"
                        onClick={() => setIsSidebarHidden(false)}
                        style={styles.topBarBtn}
                        aria-label="Abrir menú"
                    >
                        <span style={styles.burger} aria-hidden="true">
                            <span style={styles.burgerLine} />
                            <span style={styles.burgerLine} />
                            <span style={styles.burgerLine} />
                        </span>
                    </button>
                    <span style={styles.topBarTitle}>{mobileTitle}</span>
                    <button
                        type="button"
                        onClick={() => { setView('admin'); setAdminSubView('notifications'); }}
                        style={styles.topBarBtn}
                        aria-label="Notificaciones"
                    >
                        <img src={BellIcon} alt="" style={styles.notificationBellIcon} />
                        {unreadNotificationsCount > 0 && (
                            <span style={{ ...styles.notificationBadge, top: 4, right: 4 }}>{unreadNotificationsCount}</span>
                        )}
                    </button>
                </header>
            )}

            <div style={styles.mainLayout}>
                {drawerOpen && (
                    <div style={styles.drawerBackdrop} role="presentation" onClick={() => setIsSidebarHidden(true)} />
                )}
                <div
                    style={narrow
                        ? { ...styles.sidebar, ...styles.drawer, transform: isSidebarHidden ? 'translateX(-105%)' : 'none' }
                        : { ...styles.sidebar, width: isSidebarHidden ? '0px' : '272px' }}
                    aria-hidden={narrow && isSidebarHidden ? true : undefined}
                    data-cm-sidebar=""
                >
                    <div style={styles.sidebarHeader}>
                        <button type="button" className="cm-side-plain" onClick={() => setView('account')} style={styles.identity}>
                            {userData?.photoURL ? (
                                <img src={userData.photoURL} alt="" style={styles.profileImage} />
                            ) : (
                                <div style={styles.profileImagePlaceholder}>
                                    {(userData?.nombre || user.email || '?').charAt(0)}
                                </div>
                            )}
                            <span style={styles.identityText}>
                                <span style={styles.identityName}>{userData?.nombre || 'Mi cuenta'}</span>
                                <span style={styles.identityEmail}>{userData?.correo || user.email}</span>
                            </span>
                        </button>
                        <button
                            type="button"
                            className="cm-side-plain"
                            title="Notificaciones"
                            style={styles.bellBtn}
                            onClick={() => { setView('admin'); setAdminSubView('notifications'); }}
                        >
                            <img src={BellIcon} alt="" style={styles.notificationBellIcon} />
                            {unreadNotificationsCount > 0 && (
                                <span style={styles.notificationBadge}>{unreadNotificationsCount}</span>
                            )}
                        </button>
                    </div>

                    <div style={styles.menuContent}>
                        {view === 'user' && (
                            <>
                                <div style={styles.navStack}>
                                    <NavBtn active={!selectedUrl && !selectedReportId} onClick={() => goHome()}>Inicio</NavBtn>
                                    <NavBtn active={false} onClick={() => setView('account')}>Mi cuenta</NavBtn>
                                    {userPermissions.admin_panel_access && (
                                        <NavBtn
                                            active={false}
                                            onClick={() => {
                                                setView('admin');
                                                setAdminSubView('biblioteca');
                                                setAdminMenuOpen({ reportes: true, estadisticas: false, administracion: false });
                                            }}
                                        >
                                            Panel admin
                                        </NavBtn>
                                    )}
                                </div>
                                <ReportsTree
                                    menu={menuEstructurado}
                                    selectedId={selectedReport?.id || selectedReportId}
                                    onOpen={openReport}
                                />
                            </>
                        )}

                        {view === 'account' && (
                            <div style={styles.navStack}>
                                <NavBtn onClick={() => setView('user')}>‹ Volver a reportes</NavBtn>
                            </div>
                        )}

                        {view === 'admin' && (
                            <>
                                <div style={styles.navStack}>
                                    <NavBtn onClick={() => goHome()}>‹ Volver a reportes</NavBtn>
                                </div>
                                <div style={styles.adminNav}>
                                    {(userPermissions.admin_panel_users_view || userPermissions.admin_panel_groups_view || userPermissions.admin_panel_roles_view || userPermissions.admin_panel_permissions_view) && (
                                        <NavBtn
                                            active={PEOPLE_HUB_VIEWS.includes(adminSubView)}
                                            onClick={() => setAdminSubView('usuarios')}
                                        >
                                            <FaUsers size={14} />
                                            <span>Gestión de usuarios</span>
                                        </NavBtn>
                                    )}
                                    {(userPermissions.admin_panel_clients_projects_view || userPermissions.admin_panel_library_view || userPermissions.admin_panel_library_view_own || userPermissions.reports_custom_creator) && (
                                        <AdminGroup
                                            icon={<FaFileAlt size={14} />}
                                            label="Reportes"
                                            open={adminMenuOpen.reportes}
                                            onToggle={() => handleAdminMenuToggle('reportes')}
                                        >
                                            {userPermissions.admin_panel_clients_projects_view && (
                                                <NavBtn depth={1} active={adminSubView === 'clientes'} onClick={() => setAdminSubView('clientes')}>Clientes y proyectos</NavBtn>
                                            )}
                                            {(userPermissions.admin_panel_library_view || userPermissions.admin_panel_library_view_own) && (
                                                <NavBtn depth={1} active={adminSubView === 'biblioteca'} onClick={() => setAdminSubView('biblioteca')}>Biblioteca de reportes</NavBtn>
                                            )}
                                            {userPermissions.reports_custom_creator && (
                                                <NavBtn depth={1} active={adminSubView === 'creador-reportes'} onClick={() => setAdminSubView('creador-reportes')}>Creador de reportes</NavBtn>
                                            )}
                                        </AdminGroup>
                                    )}
                                    {(userPermissions.repos_social_view || userPermissions.repos_social_manage) && (
                                        <AdminGroup
                                            icon={<FaDatabase size={14} />}
                                            label="Repositorios"
                                            open={adminMenuOpen.repositorios || adminSubView === 'repos-social' || adminSubView === 'repos-perfiles'}
                                            onToggle={() => handleAdminMenuToggle('repositorios')}
                                        >
                                            {userPermissions.repos_social_view && (
                                                <NavBtn depth={1} active={adminSubView === 'repos-social'} onClick={() => setAdminSubView('repos-social')}>Redes Sociales</NavBtn>
                                            )}
                                            {userPermissions.repos_social_manage && (
                                                <NavBtn depth={1} active={adminSubView === 'repos-perfiles'} onClick={() => setAdminSubView('repos-perfiles')}>Perfiles de Redes Sociales</NavBtn>
                                            )}
                                        </AdminGroup>
                                    )}
                                    {userPermissions.admin_panel_statistics_view && (
                                        <AdminGroup
                                            icon={<FaChartLine size={14} />}
                                            label="Estadísticas"
                                            open={adminMenuOpen.estadisticas}
                                            onToggle={() => handleAdminMenuToggle('estadisticas')}
                                        >
                                            <NavBtn depth={1} active={adminSubView === 'estadisticas'} onClick={() => setAdminSubView('estadisticas')}>Estadísticas de reportes</NavBtn>
                                            <NavBtn depth={1} active={adminSubView === 'user-statistics'} onClick={() => setAdminSubView('user-statistics')}>Estadísticas de usuarios</NavBtn>
                                        </AdminGroup>
                                    )}
                                    {(userPermissions.maintenance_execute_tasks || userPermissions.admin_panel_audit_view || userPermissions.admin_panel_comprehensive_audit_view || userPermissions.admin_panel_email_templates || userPermissions.admin_panel_report_inventory || userPermissions.admin_panel_library_view || userPermissions.reports_manage_adjustments || userRole === 'admin') && (
                                        <AdminGroup
                                            icon={<FaCogs size={14} />}
                                            label="Administración"
                                            open={adminMenuOpen.administracion}
                                            onToggle={() => handleAdminMenuToggle('administracion')}
                                        >
                                            {(userPermissions.maintenance_execute_tasks || userPermissions.admin_panel_audit_view || userPermissions.admin_panel_comprehensive_audit_view) && (
                                            <NavBtn
                                                depth={1}
                                                active={adminSubView === 'mantenimiento' || adminSubView === 'auditoria'}
                                                onClick={() => setAdminSubView('mantenimiento')}
                                            >
                                                Mantenimiento
                                            </NavBtn>
                                            )}
                                            {(userRole === 'admin' || userPermissions.admin_panel_report_inventory || userPermissions.admin_panel_library_view) && (
                                                <NavBtn
                                                    depth={1}
                                                    active={adminSubView === 'inventario-reportes'}
                                                    onClick={() => setAdminSubView('inventario-reportes')}
                                                >
                                                    Inventario de reportes
                                                </NavBtn>
                                            )}
                                            {(userRole === 'admin' || userPermissions.admin_panel_library_view || userPermissions.reports_manage_adjustments) && (
                                                <NavBtn
                                                    depth={1}
                                                    active={adminSubView === 'ajustes-reportes'}
                                                    onClick={() => setAdminSubView('ajustes-reportes')}
                                                >
                                                    Ajustes de reportes
                                                </NavBtn>
                                            )}
                                            {(userRole === 'admin' || userPermissions.admin_panel_email_templates) && (
                                                <NavBtn
                                                    depth={1}
                                                    active={adminSubView === 'email-templates'}
                                                    onClick={() => setAdminSubView('email-templates')}
                                                >
                                                    Plantillas de correo
                                                </NavBtn>
                                            )}
                                        </AdminGroup>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    <div style={styles.sidebarFooter}>
                        <img src={companyLogo} alt="Logo de la Empresa" style={styles.companyLogo} />
                        <p style={styles.versionText}>Versión {APP_VERSION}</p>
                        {/* Hueco reservado para Pixel: así la mascota no tapa ningún botón del menú. */}
                        <div id="cm-pixel-dock" aria-hidden="true" style={styles.pixelDock} />
                        <button type="button" className="cm-side-plain" onClick={handleSignOut} style={styles.logoutBtn}>Cerrar sesión</button>
                    </div>
                </div>

                <div ref={mainContentRef} style={{
                    ...styles.mainContent,
                    ...(view === 'admin' && adminSubView === 'creador-reportes'
                        ? { overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }
                        : {})
                }}>
                    {view === 'admin' && <AdminPanel />}
                    {/* La Oficina Pixel solo se monta con el permiso 'office_world_view' del rol. */}
                    {userPermissions?.office_world_view === true && (
                        <AvatarWorld
                            showMap={view === 'user' && !(selectedUrl || selectedReportId)}
                            activeReport={view === 'user' ? (selectedReport || null) : null}
                        />
                    )}
                    {view === 'user' && (() => {
                        const viewing = selectedUrl || selectedReportId;
                        if (!viewing) return <DashboardGrid />;
                        if (shouldUseTestigoPdf(selectedReport)) {
                            return <iframe src={selectedReport.testigoPdf.url} style={styles.iframe} title={selectedReport.reporteNombre || 'Testigo PDF'} />;
                        }
                        if (shouldUseIframe(selectedReport)) {
                            return <ReportEmbedFrame report={selectedReport} style={styles.iframe} title={selectedReport.reporteNombre || 'Dashboard'} allowFullScreen />;
                        }
                        if (selectedReport && isCustomBuiltReport(selectedReport)) {
                            return <CustomReportRenderer report={selectedReport} />;
                        }
                        if (isExternalEmbedUrl(selectedUrl)) {
                            return <iframe src={selectedUrl} style={styles.iframe} title="Dashboard" allowFullScreen />;
                        }
                        if (selectedReportId && !selectedReport) {
                            return <div style={{ padding: '24px', color: '#5f6368' }}>Cargando reporte...</div>;
                        }
                        return <div style={{ padding: '24px', color: '#5f6368' }}>Este reporte no tiene una URL de embed válida.</div>;
                    })()}
                    {view === 'account' && <AccountPanel />}
                </div>
                {!narrow && (
                <button
                    onClick={() => setIsSidebarHidden(!isSidebarHidden)}
                    style={{ ...styles.sidebarToggleButton, left: isSidebarHidden ? '0px' : '272px' }}
                    title={isSidebarHidden ? 'Mostrar Menú' : 'Ocultar Menú'}
                >
                    {isSidebarHidden ? '›' : '‹'}
                </button>
                )}
            </div>
        </div>
    );
};

const styles = {
    appContainer: {
        display: 'flex',
        height: '100vh',
        width: '100vw',
        backgroundColor: '#FFFFFF',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
        flexDirection: 'column',
    },
    mainLayout: { display: 'flex', flex: 1, overflow: 'hidden' },
    mainContent: { flex: 1, overflow: 'auto', position: 'relative', backgroundColor: '#f6f5f8' },
    iframe: { width: '100%', height: '100%', border: 'none' },
    sidebar: {
        background: 'linear-gradient(180deg, #1b1528 0%, #161221 100%)',
        color: 'rgba(255,255,255,0.82)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.28s ease',
        overflowX: 'hidden',
        overflowY: 'auto',
        flexShrink: 0,
        borderRight: '1px solid rgba(255,255,255,0.06)',
        colorScheme: 'dark',
    },
    sidebarHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '16px 14px 14px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
    },
    identity: {
        appearance: 'none',
        border: 'none',
        background: 'transparent',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        minWidth: 0,
        flex: 1,
        padding: 0,
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
    },
    identityText: { display: 'flex', flexDirection: 'column', minWidth: 0, gap: 2 },
    identityName: {
        color: '#fff',
        fontWeight: 600,
        fontSize: '0.9rem',
        letterSpacing: '-0.02em',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    identityEmail: {
        color: 'rgba(255,255,255,0.48)',
        fontSize: '0.72rem',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    profileImage: {
        width: 40,
        height: 40,
        borderRadius: '50%',
        objectFit: 'cover',
        border: `2px solid ${ink.accent}`,
        display: 'block',
        flexShrink: 0,
    },
    profileImagePlaceholder: {
        width: 40,
        height: 40,
        borderRadius: '50%',
        backgroundColor: ink.accent,
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1rem',
        fontWeight: 700,
        flexShrink: 0,
    },
    bellBtn: {
        appearance: 'none',
        position: 'relative',
        width: 36,
        height: 36,
        borderRadius: 10,
        border: '1px solid rgba(255,255,255,0.1)',
        backgroundColor: 'rgba(255,255,255,0.06)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        padding: 0,
    },
    notificationBellIcon: { width: 16, height: 16, filter: 'brightness(0) invert(1)', opacity: 0.85 },
    notificationBadge: {
        position: 'absolute',
        top: -4,
        right: -4,
        backgroundColor: ink.danger,
        color: 'white',
        borderRadius: 999,
        padding: '1px 5px',
        fontSize: '0.65rem',
        fontWeight: 700,
        minWidth: 16,
        textAlign: 'center',
        lineHeight: 1.3,
    },
    menuContent: { flex: 1, padding: '14px 10px 18px', display: 'flex', flexDirection: 'column', gap: 6 },
    navStack: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 },
    adminNav: { display: 'flex', flexDirection: 'column', gap: 4 },
    groupBody: { display: 'flex', flexDirection: 'column', gap: 2, margin: '2px 0 8px' },
    navBtn: {
        width: '100%',
        border: 'none',
        background: 'transparent',
        color: 'rgba(255,255,255,0.72)',
        borderRadius: 10,
        padding: '9px 12px',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: '0.88rem',
        fontWeight: 500,
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        lineHeight: 1.3,
    },
    navBtnOn: {
        backgroundColor: 'rgba(94, 53, 177, 0.42)',
        color: '#fff',
        fontWeight: 600,
        boxShadow: 'inset 0 0 0 1px rgba(158, 123, 214, 0.35)',
    },
    sectionBtn: {
        appearance: 'none',
        width: '100%',
        border: 'none',
        background: 'transparent',
        color: 'rgba(255,255,255,0.92)',
        borderRadius: 10,
        padding: '9px 12px',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: '0.78rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 6,
    },
    sectionLabel: { display: 'inline-flex', alignItems: 'center', gap: 10, color: 'inherit' },
    chevron: { color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' },
    treeWrap: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        marginTop: 4,
        paddingRight: 2,
    },
    treeHead: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 12px 6px',
        fontSize: '0.72rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'rgba(255,255,255,0.42)',
    },
    treeCount: {
        fontSize: '0.68rem',
        fontWeight: 700,
        letterSpacing: 0,
        textTransform: 'none',
        color: 'rgba(255,255,255,0.32)',
    },
    tree: { display: 'flex', flexDirection: 'column', gap: 1 },
    treeLabel: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
    },
    treeClient: {
        width: '100%',
        border: 'none',
        background: 'transparent',
        color: 'rgba(255,255,255,0.78)',
        borderRadius: 8,
        padding: '7px 8px',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: '0.84rem',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 8,
        textAlign: 'left',
        boxSizing: 'border-box',
    },
    treeClientOpen: {
        color: '#fff',
    },
    treeBranch: {
        margin: '0 0 4px 12px',
        paddingLeft: 8,
        borderLeft: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
    },
    treeProject: {
        width: '100%',
        border: 'none',
        background: 'transparent',
        color: 'rgba(255,255,255,0.5)',
        borderRadius: 8,
        padding: '5px 8px',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: '0.76rem',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 8,
        textAlign: 'left',
        boxSizing: 'border-box',
    },
    treeProjectOpen: {
        color: 'rgba(255,255,255,0.82)',
    },
    treeLeaves: {
        margin: '2px 0 6px 10px',
        paddingLeft: 6,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
    },
    treeLeaf: {
        width: '100%',
        boxSizing: 'border-box',
        borderRadius: 8,
        padding: '6px 8px',
        color: 'rgba(255,255,255,0.62)',
        fontSize: '0.78rem',
        fontWeight: 500,
        cursor: 'pointer',
        fontFamily: 'inherit',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        lineHeight: 1.35,
    },
    treeLeafOn: {
        color: '#fff',
        fontWeight: 600,
    },
    sidebarFooter: {
        padding: '16px 14px 18px',
        marginTop: 'auto',
        textAlign: 'center',
        borderTop: '1px solid rgba(255,255,255,0.08)',
    },
    versionText: { fontSize: '0.72rem', color: 'rgba(255,255,255,0.36)', margin: '0 0 6px' },
    pixelDock: { height: 78, margin: '0 0 8px' },
    companyLogo: { display: 'block', width: 108, margin: '0 auto 10px' },
    logoutBtn: {
        width: '100%',
        padding: '9px 12px',
        backgroundColor: 'transparent',
        color: '#f6b6b3',
        border: '1px solid rgba(246, 182, 179, 0.35)',
        borderRadius: 10,
        cursor: 'pointer',
        fontWeight: 600,
        fontFamily: 'inherit',
        fontSize: '0.86rem',
    },
    sidebarToggleButton: {
        position: 'fixed',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 10,
        backgroundColor: '#1b1528',
        color: 'white',
        border: 'none',
        borderRadius: '0 50px 50px 0',
        width: 22,
        height: 72,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        boxShadow: '2px 0 10px rgba(22, 18, 33, 0.2)',
        transition: 'left 0.28s ease, background-color 0.2s',
    },
    /* Móvil: barra superior fija con menú (hamburguesa), título y campana. */
    topBar: {
        flexShrink: 0,
        boxSizing: 'content-box',
        height: 56,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        paddingTop: 'env(safe-area-inset-top)',
        paddingRight: 8,
        paddingBottom: 0,
        paddingLeft: 8,
        background: 'linear-gradient(180deg, #1b1528 0%, #161221 100%)',
        color: '#fff',
        boxShadow: '0 2px 10px rgba(22, 18, 33, 0.25)',
        zIndex: 30,
    },
    topBarBtn: {
        appearance: 'none',
        position: 'relative',
        width: 44,
        height: 44,
        borderWidth: 0,
        borderStyle: 'solid',
        borderRadius: 12,
        backgroundColor: 'transparent',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        flexShrink: 0,
        padding: 0,
    },
    burger: { display: 'flex', flexDirection: 'column', gap: 4, width: 20 },
    burgerLine: { display: 'block', height: 2, borderRadius: 2, backgroundColor: '#fff' },
    topBarTitle: {
        flex: 1,
        minWidth: 0,
        fontWeight: 600,
        fontSize: '1rem',
        letterSpacing: '-0.02em',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    /* Móvil: el sidebar se vuelve cajón superpuesto. */
    drawer: {
        position: 'fixed',
        top: 0,
        bottom: 0,
        left: 0,
        width: 'min(86vw, 320px)',
        zIndex: 960,
        transition: 'transform 0.26s ease',
        boxShadow: '8px 0 32px rgba(22, 18, 33, 0.35)',
        paddingBottom: 'env(safe-area-inset-bottom)',
    },
    drawerBackdrop: {
        position: 'fixed',
        inset: 0,
        zIndex: 950,
        backgroundColor: 'rgba(22, 18, 33, 0.5)',
    },
    impersonationBanner: {
        backgroundColor: ink.accent,
        color: 'white',
        padding: '10px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontWeight: 600,
    },
    impersonationButton: {
        backgroundColor: 'rgba(255,255,255,0.16)',
        color: 'white',
        border: '1px solid rgba(255,255,255,0.4)',
        padding: '6px 12px',
        borderRadius: 8,
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
};

export default MainLayout;